import { useEffect, useState, type DragEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { useSession } from "../../shell/session";
import type { Person, WorkflowBoard, WorkflowCard } from "../../../shared/types";
import { Badge, Empty, ErrorNote } from "../../ui";
import "../people/community.css";
import { canCoordinate, errorText, safeWhen, usePageTitle } from "../people/support";

export function WorkflowsPage() {
  const { user } = useSession();
  const canWrite = canCoordinate(user?.role);
  const [boards, setBoards] = useState<WorkflowBoard[]>([]);
  const [workflowId, setWorkflowId] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [personId, setPersonId] = useState("");
  const [stageId, setStageId] = useState("");
  const [over, setOver] = useState<string | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  usePageTitle("Workflows");

  useEffect(() => {
    let live = true;
    api<WorkflowBoard[]>("/api/v1/workflows")
      .then((rows) => {
        if (!live) return;
        setBoards(rows);
        setWorkflowId((current) => (current && rows.some((row) => row.id === current) ? current : rows[0]?.id ?? ""));
        setError("");
      })
      .catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    void api<Person[]>("/api/v1/people?limit=100").then(setPeople).catch(() => setPeople([]));
  }, []);

  const board = boards.find((item) => item.id === workflowId) ?? null;
  const stages = board?.stages ?? [];
  const activeStage = stages.some((stage) => stage.id === stageId) ? stageId : stages[0]?.id ?? "";

  async function reload(message: string) {
    const rows = await api<WorkflowBoard[]>("/api/v1/workflows");
    setBoards(rows);
    setWorkflowId((current) => (current && rows.some((row) => row.id === current) ? current : rows[0]?.id ?? ""));
    setNotice(message);
  }

  async function addCard(event: FormEvent) {
    event.preventDefault();
    if (!board || !activeStage) return;
    if (!title.trim()) {
      setError("Give the card a title.");
      return;
    }
    setBusy("create");
    setError("");
    try {
      await api("/api/v1/workflows/cards", {
        method: "POST",
        body: JSON.stringify({ workflowId: board.id, stageId: activeStage, title: title.trim(), personId: personId || null, notes }),
      });
      setTitle("");
      setNotes("");
      setPersonId("");
      await reload("Card added.");
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy("");
    }
  }

  async function move(cardId: string, nextStageId: string) {
    setBusy(cardId);
    setError("");
    try {
      await api(`/api/v1/workflows/cards/${cardId}/move`, { method: "POST", body: JSON.stringify({ stageId: nextStageId }) });
      await reload("Card moved.");
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy("");
      setOver(null);
    }
  }

  function dropOn(stage: string, event: DragEvent<HTMLElement>) {
    event.preventDefault();
    setOver(null);
    if (!canWrite || !board) return;
    const cardId = event.dataTransfer.getData("text/plain");
    const card = board.stages.flatMap((item) => item.cards).find((item) => item.id === cardId);
    if (!card || card.stageId === stage) return;
    void move(card.id, stage);
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Community</p>
          <h1 className="page-title">{board?.name ?? "Workflows"}</h1>
          <p className="lede">{board?.description || "Follow-up stays on the board. Nothing here sends a message."}</p>
        </div>
        {boards.length > 1 && (
          <div className="field">
            <label htmlFor="workflow-board">Board</label>
            <select id="workflow-board" value={board?.id ?? ""} onChange={(event) => setWorkflowId(event.target.value)}>
              {boards.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
        )}
      </header>
      <p className="mbc-limit">New boards, renaming stages, editing a card, and removing a card are not available. Coordinators can add a card and move it.</p>
      {!canWrite && <p className="mbc-limit">This login can read the board. Moving cards needs a coordinator, leader, or owner.</p>}
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="mbc-status" role="status">{notice}</p>}
      {loading && <p className="muted">Loading the board…</p>}
      {!loading && !board && <Empty title="No follow-up board" body="This workspace has no workflow yet, and this screen cannot create one." />}
      {canWrite && board && (
        <form className="panel panel-pad form-grid" onSubmit={(event) => void addCard(event)}>
          <h2>Add a card</h2>
          <div className="grid-2">
            <div className="field"><label htmlFor="card-title">Title</label><input id="card-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} required /></div>
            <div className="field">
              <label htmlFor="card-stage">Stage</label>
              <select id="card-stage" value={activeStage} onChange={(event) => setStageId(event.target.value)}>
                {stages.map((stage) => <option key={stage.id} value={stage.id}>{stage.name}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="card-person">Person</label>
            <select id="card-person" value={personId} onChange={(event) => setPersonId(event.target.value)}>
              <option value="">No person</option>
              {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
            </select>
          </div>
          <div className="field"><label htmlFor="card-notes">Notes</label><textarea id="card-notes" className="mbc-short" value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={2000} /></div>
          <button className="btn primary" type="submit" disabled={busy === "create" || !activeStage}>{busy === "create" ? "Saving…" : "Add card"}</button>
        </form>
      )}
      {board && (
        <div className="mbc-board" aria-label={`${board.name} stages`}>
          {board.stages.map((stage) => (
            <section
              className={over === stage.id ? "mbc-column is-over" : "mbc-column"}
              key={stage.id}
              onDragOver={(event) => { if (canWrite) { event.preventDefault(); setOver(stage.id); } }}
              onDragLeave={() => setOver((current) => current === stage.id ? null : current)}
              onDrop={(event) => dropOn(stage.id, event)}
            >
              <header>
                <h2>{stage.name}</h2>
                <Badge>{stage.cards.length}</Badge>
              </header>
              {stage.cards.length === 0 && <p className="faint">Empty stage</p>}
              {stage.cards.map((card) => (
                <CardView key={card.id} card={card} stages={board.stages} canWrite={canWrite} busy={busy === card.id} onMove={(nextStageId) => void move(card.id, nextStageId)} />
              ))}
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

function CardView({
  card,
  stages,
  canWrite,
  busy,
  onMove,
}: {
  card: WorkflowCard;
  stages: WorkflowBoard["stages"];
  canWrite: boolean;
  busy: boolean;
  onMove: (stageId: string) => void;
}) {
  return (
    <article
      className="mbc-card"
      draggable={canWrite}
      onDragStart={(event) => {
        event.dataTransfer.setData("text/plain", card.id);
        event.dataTransfer.effectAllowed = "move";
      }}
    >
      <strong>{card.title}</strong>
      {card.personId && <Link to={`/people/${card.personId}`}>{card.personName ?? "Person"}</Link>}
      {card.notes && <p className="muted">{card.notes}</p>}
      <p className="faint">{safeWhen(card.updatedAt)}</p>
      {canWrite && (
        <div className="cluster">
          {stages.filter((stage) => stage.id !== card.stageId).map((stage) => (
            <button key={stage.id} className="btn small" type="button" disabled={busy} onClick={() => onMove(stage.id)}>
              {busy ? "Moving…" : `Move to ${stage.name}`}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}
